import { Text, Button, Textarea, Flex, ClickableItem, AssistChip, Tooltip, OutsideClick } from '@upsales/components';
import { sendMessage, setMessage, retryMessage } from './Context/Helpers';
import { AIChatProvider, useAIChatDispatch, useAIChatSelector } from './Context/AIChatContext';
import { useTranslation } from 'Components/Helpers/translate';
import { ActionComponent } from './ActionComponents';
import BemClass from '@upsales/components/Utils/bemClass';
import { setAIChatOpen } from 'Store/actions/AppActions';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../hooks';
import { RootState } from 'Store/index';
import Markdown from 'Components/Markdown/Markdown';
import { useSelf } from 'App/components/hooks/appHooks';
import SuggestedQuestions from './SuggestedQuestions';
import { SpeechRecognitionButton } from './SpeechRecognitionButton';
import AIChatResource from 'Resources/AIChat';
import FeedbackButtons from './FeedbackButtons';
import { useDraggableChat } from './useDraggableChat';
import Sidebar from './SideBar/Sidebar';
import { useAIChatListeners } from './useAIChatListeners';
import { SnapHotspot } from './SnapHotspot';
import Reasonings from './Reasonings';
import { LoadingIndicator } from './LoadingIndicator';

import './AIChat.scss';

interface ContextObject {
	id: number;
	entity: string;
	label: string;
	timestamp: number;
}

const StartView = ({
	onQuestionSelect,
	resetKey,
	className,
	spacing
}: {
	onQuestionSelect?: () => void;
	resetKey?: number;
	className?: string;
	spacing?: string;
}) => {
	const { t } = useTranslation();
	const self = useSelf();
	const name = self?.name?.split(' ')[0] || self?.name;

	return (
		<Flex direction="column" spacing={spacing || 'pl4 pr4'} gap="u2" className={className}>
			<Flex direction="column" gap="u1" alignItems="center">
				<Text bold>{`${t('default.hello')} ${name}`}</Text>
				<Text>{t('aiChat.howCanIHelpYou')}</Text>
			</Flex>
			<SuggestedQuestions resetKey={resetKey} onQuestionSelect={onQuestionSelect} />
		</Flex>
	);
};

const AIChat = () => {
	const classes = new BemClass('AIChat');

	const { messages, loading, message, currentChatId, chats, isChatSidebarExpanded } = useAIChatSelector(
		({ messages, loading, message, currentChatId, chats, isChatSidebarExpanded }) => ({
			messages,
			loading: currentChatId ? loading[currentChatId] || false : false,
			message,
			currentChatId,
			chats,
			isChatSidebarExpanded
		})
	);
	const dispatch = useAIChatDispatch();
	const textareaRef = useRef<HTMLTextAreaElement>();
	const { t } = useTranslation();
	const [currentContext, setCurrentContext] = useState<ContextObject | null>(null);

	const aichatOpen = useSelector(({ App }: RootState) => App.aichatOpen);
	const dispatchRedux = useAppDispatch();

	const [isTextareaFocused, setIsTextareaFocused] = useState(false);
	const [showStartMenu, setShowStartMenu] = useState(false);
	const [startMenuResetKey, setStartMenuResetKey] = useState(0);
	const [animationState, setAnimationState] = useState<'opening' | 'open' | 'closing' | 'closed'>('closed');
	const [shouldExpandWidth, setShouldExpandWidth] = useState(false);

	// Use draggable chat hook
	const {
		position,
		width,
		height,
		isDragging,
		isResizing,
		resizeDirection,
		snapHotspot,
		containerWidth,
		containerRef: chatContainerRef,
		handleMouseDown,
		handleDoubleClick,
		isMaximized,
		getResizeDirection,
		getResizeCursor
	} = useDraggableChat({
		isOpen: aichatOpen,
		onToggle: () => dispatchRedux(setAIChatOpen(!aichatOpen)),
		onClose: () => dispatchRedux(setAIChatOpen(false))
	});

	// Handle animation states when opening/closing
	useEffect(() => {
		if (aichatOpen) {
			setAnimationState('opening');
			// Small delay to ensure the DOM is ready before starting animation
			requestAnimationFrame(() => {
				setAnimationState('open');
			});
		} else if (animationState !== 'closed') {
			setAnimationState('closing');
			// Wait for close animation to complete before unmounting
			const timer = setTimeout(() => {
				setAnimationState('closed');
			}, 300); // Match animation duration
			return () => clearTimeout(timer);
		}
	}, [aichatOpen]);

	// Determine if chat should expand width when sidebar expands (only when expanding, not when moving)
	useEffect(() => {
		if (isChatSidebarExpanded) {
			const chatWidth = width || 500;
			const chatCenterX = position.x + chatWidth / 2;
			const screenCenterX = window.innerWidth / 2;
			setShouldExpandWidth(chatCenterX < screenCenterX);
		}
	}, [isChatSidebarExpanded]);

	const handleSendMessage = () => {
		if (!loading) {
			setShowStartMenu(false);
			dispatch(sendMessage());
		}
	};

	const handleSetMessage = (message: string) => {
		dispatch(setMessage(message));
	};

	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const previousMessagesLength = useRef<number>(0);
	const previousLoading = useRef<boolean>(false);
	const previousLastMessageResponse = useRef<string>('');

	// Set initial scroll position to bottom when chat opens (without animation)
	useEffect(() => {
		if (aichatOpen && messagesContainerRef.current && messages.length > 0) {
			// Only set initial position, don't animate
			requestAnimationFrame(() => {
				if (messagesContainerRef.current) {
					messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
				}
			});
		}
	}, [aichatOpen, messages.length]);

	// Auto-scroll to bottom only when new messages arrive or when streaming updates (not when opening/closing)
	useEffect(() => {
		const hasNewMessages = messages.length > previousMessagesLength.current;
		const isLoadingNewMessage = loading && !previousLoading.current;

		// Check if last message is streaming or has updated response
		const lastMessage = messages[messages.length - 1];
		const isStreaming = lastMessage?.streaming === true;
		const responseUpdated = lastMessage?.response && lastMessage.response !== previousLastMessageResponse.current;

		if (
			messagesContainerRef.current &&
			!showStartMenu &&
			(hasNewMessages || isLoadingNewMessage || isStreaming || responseUpdated)
		) {
			setTimeout(() => {
				messagesContainerRef?.current?.scrollTo({
					top: messagesContainerRef?.current?.scrollHeight,
					behavior: 'smooth'
				});
			}, 100); // Shorter timeout for streaming updates
		}
		// Update previous values
		previousMessagesLength.current = messages.length;
		previousLoading.current = loading;
		previousLastMessageResponse.current = lastMessage?.response || '';
	}, [messages, loading, showStartMenu]);

	// Scroll to bottom smoothly after resize is done
	const prevIsResizing = useRef(isResizing);
	useEffect(() => {
		// When resize ends (isResizing changes from true to false)
		if (prevIsResizing.current && !isResizing && messagesContainerRef.current) {
			messagesContainerRef.current.scrollTo({
				top: messagesContainerRef.current.scrollHeight,
				behavior: 'smooth'
			});
		}
		prevIsResizing.current = isResizing;
	}, [isResizing]);

	useAIChatListeners(setCurrentContext);

	const clickedOnSidebar = useRef(false);

	useEffect(() => {
		if (animationState === 'closed') return;

		const handleMouseDown = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			clickedOnSidebar.current = !!target?.closest('.AppSidebar');
		};

		document.addEventListener('mousedown', handleMouseDown, true);
		return () => document.removeEventListener('mousedown', handleMouseDown, true);
	}, [animationState]);

	const onOutsideClick = () => {
		if (!clickedOnSidebar.current) {
			dispatchRedux(setAIChatOpen(false));
		}
		clickedOnSidebar.current = false;
	};

	if (animationState === 'closed') {
		return null;
	}

	return (
		<OutsideClick targetId="ai-chat-container" outsideClick={onOutsideClick}>
			<SnapHotspot position="left" width={containerWidth} isVisible={isDragging && snapHotspot === 'left'} />
			<SnapHotspot position="right" width={containerWidth} isVisible={isDragging && snapHotspot === 'right'} />
			<Flex
				ref={chatContainerRef}
				id="ai-chat-container"
				className={`${classes
					.mod({ open: animationState === 'open', close: animationState === 'closing' })
					.b()} ${classes.elem('container').mod({ dragging: isDragging, resizing: isResizing }).b()}`}
				direction="column"
				justifyContent="space-between"
				style={{
					left: `${position.x}px`,
					top: `${position.y}px`,
					cursor: isResizing ? getResizeCursor(resizeDirection) : undefined,
					...(width !== null && {
						width: `${width + (isChatSidebarExpanded && shouldExpandWidth && !isMaximized ? 160 : 0)}px`
					}),
					...(height !== null && { height: `${height}px` })
				}}
				onMouseDown={e => {
					// Check if clicking on a resize area (edges/corners)
					const resizeDir = getResizeDirection(e);
					if (resizeDir) {
						// Handle resize from edges
						handleMouseDown(e);
					}
					// If not a resize area and not on header, let header handle it
				}}
				onMouseMove={e => {
					// Update cursor when hovering over resize areas
					if (!isDragging && !isResizing && chatContainerRef.current) {
						const resizeDir = getResizeDirection(e);
						if (resizeDir) {
							chatContainerRef.current.style.cursor = getResizeCursor(resizeDir);
						} else {
							// Check if hovering over header
							const target = e.target as HTMLElement;
							const headerElement = chatContainerRef.current.querySelector(
								`.${classes.elem('header').b()}`
							);
							const isOverHeader = headerElement && headerElement.contains(target);
							if (isOverHeader && !isMaximized) {
								chatContainerRef.current.style.cursor = 'grab';
							} else {
								// Remove cursor style to let CSS handle it
								chatContainerRef.current.style.removeProperty('cursor');
							}
						}
					}
				}}
				onMouseLeave={() => {
					if (!isDragging && !isResizing && chatContainerRef.current) {
						chatContainerRef.current.style.removeProperty('cursor');
					}
				}}
			>
				<Flex
					alignItems="center"
					justifyContent="space-between"
					spacing="pl4"
					className={`${classes.elem('header').b()} ${isMaximized ? '' : classes.elem('headerGrab').b()}`}
					onMouseDown={e => {
						// Don't allow dragging when maximized
						if (isMaximized) return;

						// Only start dragging if clicking on the header itself, not on buttons
						// Also check if it's not a resize area (edges)
						const target = e.target as HTMLElement;
						const resizeDir = getResizeDirection(e);
						if (
							!resizeDir &&
							!target.closest('button') &&
							!target.closest('[role="button"]') &&
							!target.closest('.ClickableItem')
						) {
							handleMouseDown(e);
						}
					}}
					onDoubleClick={e => {
						// Only maximize if double-clicking on the header itself, not on buttons
						const target = e.target as HTMLElement;
						if (
							!target.closest('button') &&
							!target.closest('[role="button"]') &&
							!target.closest('.ClickableItem')
						) {
							handleDoubleClick();
						}
					}}
				>
					<Flex
						alignItems="center"
						justifyContent="space-between"
						gap="u2"
						className={classes.elem('headerContent').b()}
					>
						<Flex alignItems="center" gap="u2">
							<AssistChip title={t('aiChat.upsalesAI')} icon="stars" type="purple" />
							<Text ellipsis bold>
								{currentChatId
									? chats.find(c => c.id === currentChatId)?.name || t('aiChat.title')
									: t('aiChat.title')}
							</Text>
						</Flex>
						<Flex alignItems="center">
							<ClickableItem onClick={() => dispatchRedux(setAIChatOpen(false))} icon="minus" />
							<Tooltip title={isMaximized ? t('default.minimize') : t('default.maximize')}>
								<ClickableItem onClick={handleDoubleClick} icon="expand" />
							</Tooltip>
						</Flex>
					</Flex>
				</Flex>

				<Flex direction="row" className={classes.elem('mainContent').b()}>
					<Sidebar
						setShowStartMenu={setShowStartMenu}
						showStartMenu={showStartMenu}
						setStartMenuResetKey={setStartMenuResetKey}
					/>
					<Flex direction="column" justifyContent="flex-end" className={classes.elem('contentArea').b()}>
						{showStartMenu || messages.length === 0 ? (
							<StartView
								className={classes.elem('emptyState').b()}
								resetKey={startMenuResetKey}
								onQuestionSelect={() => {
									setShowStartMenu(false);
									setStartMenuResetKey(prev => prev + 1);
								}}
							/>
						) : (
							<Flex
								ref={messagesContainerRef}
								direction="column"
								className={classes.elem('messages').b()}
								spacing="pl6 pr6 pb6"
								gap="u2"
								onWheel={e => {
									e.stopPropagation();
								}}
								onTouchMove={e => {
									e.stopPropagation();
								}}
							>
								{messages.map((message, index) => {
									const shouldShowFeedback =
										!message.streaming && !message.loading;
									const hasAction = !!message.action;
									const reasonings = message.reasoning ? message.reasoning.split('\n') : [];

									return (
										<React.Fragment key={index}>
											{message.message ? (
												<Flex justifyContent="flex-end" alignItems="center" gap="u2">
													<Text className={classes.elem('userMessage').b()}>
														{message.message}
													</Text>
													{message.hasError ? (
														<Tooltip title={t('default.retry')}>
															<ClickableItem
																icon="refresh"
																onClick={() => dispatch(retryMessage(message.message))}
																size="sm"
															/>
														</Tooltip>
													) : null}
												</Flex>
											) : null}

											{message.reasoning ? (
												<Reasonings
													key={`reasoning-${currentChatId}-${message.id || index}`}
													reasonings={reasonings}
												/>
											) : null}

											{(message.streaming || !message.loading) && message.response ? (
												<Flex direction="column" justifyContent="flex-start">
													<div
														className={classes
															.elem('aiMessage')
															.mod({ streaming: message.streaming, hasAction })
															.b()}
													>
														<Markdown
															markdown={message.response || ''}
															compact
															enableEntityLinks
														/>
													</div>
													{shouldShowFeedback && !hasAction ? (
														<FeedbackButtons messageIndex={index} />
													) : null}
												</Flex>
											) : null}

											{message.loading ? <LoadingIndicator /> : null}

											{message.action ? (
												<Flex direction="column" gap="u1">
													<ActionComponent
														action={message.action}
														onMinimizeChat={() => dispatchRedux(setAIChatOpen(false))}
													/>
													{shouldShowFeedback ? (
														<FeedbackButtons messageIndex={index} />
													) : null}
												</Flex>
											) : null}
										</React.Fragment>
									);
								})}
							</Flex>
						)}

						<Flex
							direction="column"
							spacing="ml4 mr4 pt1 pr2 pb2 pl2"
							className={classes.elem('inputContainer').mod({ focused: isTextareaFocused }).b()}
						>
							<Textarea
								textareaRef={r => (textareaRef.current = r)}
								data-gramm="false"
								// @ts-ignore
								autoFocus
								placeholder={t('aiChat.askUpsalesAI')}
								value={message}
								autoHeight
								inputClassName={classes.elem('textarea').b()}
								onChange={e => handleSetMessage(e.target.value)}
								onFocus={() => setIsTextareaFocused(true)}
								onBlur={() => setIsTextareaFocused(false)}
								onKeyDown={e => {
									if (e.key === 'Enter' && message.trim() && !e.shiftKey) {
										e.preventDefault();
										handleSendMessage();
									}
								}}
							/>
							<Flex spacing="mt2" justifyContent="space-between" alignItems="center">
								{currentContext ? (
									<div className={classes.elem('context').b()}>
										<Tooltip title={currentContext.label}>
											<AssistChip
												type="purple"
												title={`${t(`default.${currentContext.entity.toLowerCase()}`)} #${
													currentContext.id
												} - ${currentContext.label}`}
												onHide={async () => {
													try {
														await AIChatResource.clearContext();
														setCurrentContext(null);
													} catch {
														setCurrentContext(null);
													}
												}}
											/>
										</Tooltip>
									</div>
								) : (
									<div />
								)}
								<Flex gap="u2" justifyContent="flex-end" alignItems="center">
									<SpeechRecognitionButton />
									<Button
										onClick={handleSendMessage}
										className={classes.elem('sendMessage').b()}
										loading={loading}
										disabled={!message}
										icon="arrow-up"
										size="sm"
									/>
								</Flex>
							</Flex>
						</Flex>
						<Flex justifyContent="center" spacing="mb4 mt1">
							<Text size="sm" color="grey-7">
								{t('ai.chat.disclaimer')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Flex>
		</OutsideClick>
	);
};

const connectProvider = (props: Record<string, never>) => {
	return (
		<AIChatProvider>
			<AIChat {...props} />
		</AIChatProvider>
	);
};

export default connectProvider;
